/**
 * Bundle Subscription Manager
 * ===========================
 * Manages bundle subscription UI: counter display, progress, continue button state.
 * Listens to smart-buy:change events to update UI in real-time.
 *
 * Dependencies:
 * - smart-buy-button.js (emits smart-buy:change)
 * - bundle-checkout.js (handles submission)
 * - bundle-config.liquid (provides BundleConfig)
 */

(() => {
  const SMART_BUY_EVENT = 'smart-buy:change';
  // Internal hooks: keep modules loosely coupled.
  const CART_UPDATED_EVENT = 'bundle-subscription:cart-updated';
  // Fired when bottom purchase bar HTML is replaced (AJAX section updates).
  const BAR_REFRESHED_EVENT = 'bottom-purchase-bar:refreshed';
  /** Redirect target when step 2/3 has no bundle in cart */
  const SUBSCRIPTION_STEP_1_PATH = '/pages/subscription-step-1';
  /** Modes where bundleBasket is actively managed (selection + Recharge conversion). */
  const BUNDLE_ACTIVE_MODES = ['subscription_step_1', 'subscription_step_2'];

  /**
   * BundleSubscription - Configuration and state management
   */
  window.BundleSubscription = window.BundleSubscription || {};

  // Bundle rules (can be overridden by BundleConfig)
  window.BundleSubscription.mainDishesRequired =
    window.BundleConfig?.minMainDishes || 3;
  window.BundleSubscription.maxMainDishes =
    window.BundleConfig?.maxMainDishes || 30;

  let hasBoundListeners = false;
  let cartUpdateUnsubscriber = null;
  // Last server cart payload (used to re-apply totals after the bar DOM is replaced).
  let lastCartData = null;
  const purchaseBarUpdates = new WeakMap();

  function getBar() {
    return document.querySelector('.bottom-purchase-bar');
  }

  function ensureBarConfig(bar) {
    if (!bar) return;
    // Keep Liquid as source of truth if present; otherwise set a sane default.
    if (!bar.dataset.mainDishesRequired) {
      bar.dataset.mainDishesRequired = String(window.BundleSubscription.mainDishesRequired || 5);
    }
  }

  /**
   * Get count of primary items in bundleBasket (only items with isPrimary == true
   * count toward the minimum-dishes requirement).
   * @returns {number} Total quantity of primary bundle items
   */
  function getBundleCount() {
    if (!window.bundleBasket) return 0;

    return Object.values(window.bundleBasket).reduce((sum, item) => {
      if (typeof item !== 'object') return sum;
      if (!item.isPrimary) return sum;
      return sum + (item.quantity || 0);
    }, 0);
  }

  /**
   * Get total count of ALL items in bundleBasket (regardless of isPrimary).
   * Used for the max-dishes limit which applies to the entire bundle.
   * @returns {number} Total quantity across all bundle items
   */
  function getBundleTotalCount() {
    if (!window.bundleBasket) return 0;

    return Object.values(window.bundleBasket).reduce((sum, item) => {
      const qty = typeof item === 'object' ? (item.quantity || 0) : (Number(item) || 0);
      return sum + qty;
    }, 0);
  }

  /**
   * Calculate total price of ALL items in bundleBasket (in-memory).
   * Used when the full bundle total is needed (e.g. step 2 price display).
   * @returns {number} Total price in cents
   */
  function getBundleBasketTotalPrice() {
    if (!window.bundleBasket) return 0;

    return Object.values(window.bundleBasket).reduce((sum, item) => {
      if (typeof item !== 'object') return sum;
      const price = (item.price || 0) + (item.surcharges || 0);
      return sum + price * (item.quantity || 0);
    }, 0);
  }

  /**
   * Calculate total price of PRIMARY items only in bundleBasket.
   * Used in step 1 so the displayed price matches the counter (both filter by isPrimary).
   * @returns {number} Total price in cents (primary items only)
   */
  function getBundlePrimaryTotalPrice() {
    if (!window.bundleBasket) return 0;

    return Object.values(window.bundleBasket).reduce((sum, item) => {
      if (typeof item !== 'object') return sum;
      if (!item.isPrimary) return sum;
      const price = (item.price || 0) + (item.surcharges || 0);
      return sum + price * (item.quantity || 0);
    }, 0);
  }

  function isBundleCartItem(cartItem) {
    const props = cartItem?.properties;
    if (!props) return false;
    // Recharge bundle items are identified by one of these properties.
    // - _rc_bundle: Recharge bundle group id
    // - _rb_id: Recharge legacy bundle id (or other bundle grouping id)
    return Boolean(props._rc_bundle) || Boolean(props._rb_id);
  }

  function isSurchargeCartItem(cartItem) {
    const props = cartItem?.properties;
    // Some implementations add a dedicated surcharge line with properties._type = 'surcharge'.
    return props?._type === 'surcharge';
  }

  /**
   * Returns true if cart has at least one bundle item (Recharge _rc_bundle / _rb_id).
   * Used to redirect from step 2/3 to step 1 when the bundle is removed.
   */
  function cartHasBundle(items) {
    if (!Array.isArray(items)) return false;
    return items.some((item) => isBundleCartItem(item));
  }

  /**
   * If we are on subscription step 2 or 3 and the cart has no bundle, redirect to step 1.
   */
  function redirectToStep1IfNoBundle(bar, items) {
    const mode = bar?.dataset?.mode;
    if (mode !== 'subscription_step_2' && mode !== 'subscription_step_3') return;
    if (cartHasBundle(items)) return;
    window.location.replace(SUBSCRIPTION_STEP_1_PATH);
  }

  function isSubscriptionCartItem(cartItem) {
    // Shopify cart.js includes selling_plan_allocation for subscription lines.
    return cartItem?.selling_plan_allocation != null;
  }

  async function fetchCartJson() {
    const url =
      (window.routes && window.routes.cart_url)
        ? `${window.routes.cart_url}.js`
        : '/cart.js';

    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`Failed to fetch cart (${res.status})`);
    return res.json();
  }

  /**
   * Re-hydrate window.bundleBasket from current cart (bundle lines only).
   * Mirrors what bundle-config.liquid does on full page load. Call when returning to step 1
   * via browser back so UI (cards, bar) matches cart instead of stale empty in-memory state.
   */
  function buildBundleBasketFromCartItems(items) {
    if (!Array.isArray(items)) return {};
    const byVariant = {};
    items.forEach((item) => {
      if (!isBundleCartItem(item) || isSurchargeCartItem(item)) return;
      const vid = String(item.variant_id);
      const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id ?? null;
      const sellingPlanIds = sellingPlanId ? [sellingPlanId] : [];
      const price = item.price != null ? item.price : 0;
      if (!byVariant[vid]) {
        byVariant[vid] = {
          quantity: 0,
          variantId: vid,
          productId: item.product_id != null ? String(item.product_id) : null,
          title: item.title || '',
          image: item.image || '',
          price,
          surcharges: 0,
          sellingPlanIds,
          isPrimary: true,
          collectionId: item.properties?._rc_bundle_collection_id ?? null
        };
      }
      byVariant[vid].quantity += item.quantity;
    });
    enrichBundleBasketFromDOM(byVariant);
    return byVariant;
  }

  /**
   * Enrich basket entries with isPrimary from product card DOM attributes.
   * Falls back to true for items without a matching card (conservative: count them).
   */
  function enrichBundleBasketFromDOM(basket) {
    const cards = document.querySelectorAll('.card-wrapper[data-bundle-mode]');
    const metaByVariant = new Map();
    cards.forEach((card) => {
      const vid = card.dataset.variantId;
      if (vid) {
        metaByVariant.set(String(vid), {
          isPrimary: card.dataset.bundlePrimary === 'true',
          collectionId: card.dataset.collectionId || null
        });
      }
    });
    Object.keys(basket).forEach((vid) => {
      if (metaByVariant.has(vid)) {
        const meta = metaByVariant.get(vid);
        basket[vid].isPrimary = meta.isPrimary;
        if (meta.collectionId && !basket[vid].collectionId) {
          basket[vid].collectionId = meta.collectionId;
        }
      }
    });
  }

  async function hydrateBundleBasketFromCart() {
    const bar = getBar();
    if (!bar || !BUNDLE_ACTIVE_MODES.includes(bar.dataset.mode)) return;
    try {
      const cart = await fetchCartJson();
      const items = Array.isArray(cart?.items) ? cart.items : [];
      window.bundleBasket = buildBundleBasketFromCartItems(items);
      if (window.SmartBuyStore) {
        window.SmartBuyStore.ensureBundleState();
      }
      window.dispatchEvent(
        new CustomEvent(SMART_BUY_EVENT, { detail: { source: 'bundle-hydrate' } })
      );
      lastCartData = cart;
      ensureBarConfig(bar);
      await updatePurchaseBar(bar, cart);
    } catch (e) {
      console.error('[bundle-subscription] hydrateBundleBasketFromCart failed:', e);
    }
  }

  /**
   * Calculate subscription total for bundle-active steps (step 1 & step 2):
   * - Bundle items (in-memory bundleBasket — original step1 selection + step2 additions)
   * - Subscription "extras" already in the real cart that are NOT bundle items
   *
   * The minimum dish counter still counts ONLY bundleBasket items (separate concern).
   * @param {Object|null} cartData Optional cart JSON (from PUB_SUB_EVENTS.cartUpdate)
   * @returns {Promise<{totalPrice: number, couponSavings: number}>} Amounts in cents
   */
  async function getSubscriptionBundlePricing(cartData = null, mode = 'subscription_step_1') {
    // Step 1 shows only primary items until the minimum count is reached;
    // from that point (and in step 2), it shows the complete bundle.
    const bundleTotal = mode === 'subscription_step_1' &&
      getBundleCount() < window.BundleConfig.minMainDishes
      ? getBundlePrimaryTotalPrice()
      : getBundleBasketTotalPrice();

    let cart = cartData;
    if (!Array.isArray(cart?.items)) {
      try {
        cart = await fetchCartJson();
      } catch (e) {
        console.error('[bundle-subscription] Failed to fetch cart JSON:', e);
        return { totalPrice: bundleTotal, couponSavings: 0 };
      }
    }

    const items = Array.isArray(cart?.items) ? cart.items : [];
    const pricing = { totalPrice: bundleTotal, couponSavings: 0 };
    for (const item of items) {
      if (!isSubscriptionCartItem(item) || isSurchargeCartItem(item) || isBundleCartItem(item)) continue;
      // Extra subscription line (not part of the bundle group).
      pricing.totalPrice += item.final_line_price ?? item.line_price ?? 0;
      for (const allocation of item.line_level_discount_allocations || []) {
        if (allocation.discount_application?.type === 'discount_code' && allocation.amount > 0) {
          pricing.couponSavings += allocation.amount;
        }
      }
    }

    return pricing;
  }

  async function getSubscriptionBundleTotalPrice(cartData = null, mode = 'subscription_step_1') {
    return (await getSubscriptionBundlePricing(cartData, mode)).totalPrice;
  }

  function updateContinueButton(bar) {
    if (!bar || bar.dataset.mode !== 'subscription_step_1') return;

    const button = bar.querySelector('.bottom-purchase-bar__continue-button');
    if (!button) return;

    const required = parseInt(bar.dataset.mainDishesRequired, 10) || window.BundleSubscription.mainDishesRequired;
    const maximum = parseInt(bar.dataset.maxMainDishes, 10) || window.BundleSubscription.maxMainDishes;
    const count = getBundleCount();
    const remaining = Math.max(0, window.BundleConfig.minOrderAmountCents - getBundleBasketTotalPrice());
    const belowMinimum = count < required || remaining > 0;
    const exceedsMaximum = count > maximum;

    button.classList.toggle('is-disabled', exceedsMaximum);
    if (exceedsMaximum) {
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('tabindex', '-1');
    } else {
      button.removeAttribute('aria-disabled');
      button.removeAttribute('tabindex');
    }

    if (belowMinimum && !exceedsMaximum) {
      button.dataset.modalOpen = 'pedido-minimo';
    } else {
      delete button.dataset.modalOpen;
    }

    const notice = bar.querySelector('.bottom-purchase-bar__minimum-notice');
    if (notice) {
      notice.hidden = !belowMinimum || exceedsMaximum;
      const amount = (remaining / 100).toFixed(2).replace('.', ',') + '€';
      notice.textContent = remaining > 0
        ? notice.dataset.amountMessage.replace('[amount]', amount)
        : notice.dataset.dishesMessage;
    }
  }

  async function updatePurchaseBar(bar, cartData = null) {
    if (!bar) return;
    const updateId = (purchaseBarUpdates.get(bar) || 0) + 1;
    purchaseBarUpdates.set(bar, updateId);

    ensureBarConfig(bar);
    updateContinueButton(bar);

    const required = parseInt(bar.dataset.mainDishesRequired, 10) ||
      window.BundleSubscription.mainDishesRequired;
    const maxMainDishes = parseInt(bar.dataset.maxMainDishes, 10) ||
      window.BundleSubscription.maxMainDishes ||
      (window.BundleConfig?.maxMainDishes ?? 30);
    const primaryCount = getBundleCount();
    const totalCount = getBundleTotalCount();
    // Step 1: primary-only bundle total + cart subscription extras.
    // Step 2: full bundle total + cart subscription extras.
    const mode = bar.dataset.mode;
    const pricing = BUNDLE_ACTIVE_MODES.includes(mode)
      ? await getSubscriptionBundlePricing(cartData, mode)
      : { totalPrice: getBundleBasketTotalPrice(), couponSavings: 0 };
    if (!bar.isConnected || purchaseBarUpdates.get(bar) !== updateId) return;
    const { totalPrice, couponSavings } = pricing;

    // Update data attributes
    bar.dataset.mainDishesCount = primaryCount;
    bar.dataset.bundleTotalPrice = totalPrice;

    // Update counter: only primary items count toward the requirement
    const counterEl = bar.querySelector('.bottom-purchase-bar__counter');
    if (counterEl) {
      const isComplete = primaryCount >= required;
      const exceedsRequired = primaryCount > required;
      counterEl.classList.toggle('is-complete', isComplete);
      counterEl.classList.toggle('bottom-purchase-bar__counter--exceeds', exceedsRequired);

      const counterValue = counterEl.querySelector('.bottom-purchase-bar__counter-value');
      const counterRequired = counterEl.querySelector('.bottom-purchase-bar__counter-required');
      if (counterValue) counterValue.textContent = primaryCount;
      if (counterRequired) counterRequired.textContent = required;
    }

    // Update price display (if exists)
    const priceEl = bar.querySelector('.bottom-purchase-bar__price-value');
    if (priceEl) {
      priceEl.textContent = (totalPrice / 100).toFixed(2).replace('.', ',') + '€';
    }

    // Max-limit body class: only primary items count toward the dish limit
    if (bar.dataset.mode === 'subscription_step_1') {
      document.body.classList.toggle('bundle-limit-reached', primaryCount >= maxMainDishes);
    }

    // Dispatch custom event for other components
    window.dispatchEvent(
      new CustomEvent('bundle:updated', {
        detail: { bar, count: primaryCount, required, totalPrice, couponSavings, isComplete: primaryCount >= required }
      })
    );
  }

  /**
   * Initialize bundle subscription listeners
   */
  function initBundleSubscription() {
    const bar = getBar();
    if (bar) {
      ensureBarConfig(bar);
      const mode = bar.dataset.mode;

      if (mode === 'subscription_step_2') {
        // Step 2: re-hydrate bundleBasket from cart (step1 items already there as
        // Recharge lines), then update bar price. Also checks for redirect if no bundle.
        fetchCartJson()
          .then((cart) => {
            const items = Array.isArray(cart?.items) ? cart.items : [];
            redirectToStep1IfNoBundle(bar, items);
            window.bundleBasket = buildBundleBasketFromCartItems(items);
            if (window.SmartBuyStore) window.SmartBuyStore.ensureBundleState();
            window.dispatchEvent(
              new CustomEvent(SMART_BUY_EVENT, { detail: { source: 'bundle-hydrate' } })
            );
            lastCartData = cart;
            ensureBarConfig(bar);
            updatePurchaseBar(bar, cart);
          })
          .catch((e) => console.error('[bundle-subscription] Step 2 init failed:', e));
      } else if (mode === 'subscription_step_3') {
        // Step 3: only check redirect guard (no bundleBasket management).
        fetchCartJson()
          .then((cart) => {
            const items = Array.isArray(cart?.items) ? cart.items : [];
            redirectToStep1IfNoBundle(bar, items);
          })
          .catch((e) => console.error('[bundle-subscription] Step 3 cart check failed:', e));
        updatePurchaseBar(bar);
      } else {
        // Step 1 (and any other mode): just update bar.
        updatePurchaseBar(bar);
      }
    }

    // Bind listeners once; DOM can be replaced via AJAX sections.
    if (!hasBoundListeners) {
      hasBoundListeners = true;

      // 1) Bundle changes (in-memory) will update count + total on step 1 & step 2.
      window.addEventListener(SMART_BUY_EVENT, (event) => {
        const currentBar = getBar();
        if (!currentBar) return;
        const detail = event?.detail || {};
        const mode = currentBar.dataset.mode;
        // React on bundle-active steps (step 1/2) or any bundle-mode interaction.
        if (!BUNDLE_ACTIVE_MODES.includes(mode) && detail.mode !== 'bundle') return;
        ensureBarConfig(currentBar);
        updatePurchaseBar(currentBar);
      });

      // 2) Cart updates must re-sync AFTER the bar section HTML is replaced.
      // When adding a non-bundle subscription (e.g. postre), smart-buy-button re-renders the bar
      // via AJAX, which resets it to Liquid values ("0 de 5" and only postre total).
      // We use getBar() to always query the current DOM, then re-apply JS-calculated values.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        cartUpdateUnsubscriber?.();
        cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const currentBar = getBar();
          if (!currentBar) return;
          const mode = currentBar.dataset.mode;

          if (BUNDLE_ACTIVE_MODES.includes(mode)) {
            // Step 1 & 2: save cart data and re-apply JS-calculated values.
            lastCartData = event?.cartData || null;
            ensureBarConfig(currentBar);
            updatePurchaseBar(currentBar, lastCartData);
            window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT, { detail: event || {} }));

            // Step 2: also check redirect guard when bundle is removed.
            if (mode === 'subscription_step_2') {
              const items = event?.cartData?.items;
              if (Array.isArray(items)) redirectToStep1IfNoBundle(currentBar, items);
            }
          } else if (mode === 'subscription_step_3') {
            // Step 3: only redirect guard.
            const items = event?.cartData?.items;
            if (Array.isArray(items)) redirectToStep1IfNoBundle(currentBar, items);
          }
        });
      }

      // 3) When the bar section is re-rendered (AJAX), re-apply JS-calculated values.
      window.addEventListener(BAR_REFRESHED_EVENT, (event) => {
        const currentBar = getBar();
        if (!currentBar) return;
        if (!BUNDLE_ACTIVE_MODES.includes(currentBar.dataset.mode)) return;
        ensureBarConfig(currentBar);
        lastCartData = event.detail?.cartData || null;
        updatePurchaseBar(currentBar, lastCartData);
      });
    }

    // Listen for bundle checkout events
    if (window.BUNDLE_CHECKOUT_EVENT) {
      window.addEventListener(window.BUNDLE_CHECKOUT_EVENT, (event) => {
        const { type } = event.detail || {};
        if (type === 'start') {
          // Show loading state on continue button
          const continueBtn = bar?.querySelector('.bottom-purchase-bar__continue-button');
          if (continueBtn) {
            continueBtn.classList.add('loading');
          }
        } else if (type === 'error') {
          // Remove loading state
          const continueBtn = bar?.querySelector('.bottom-purchase-bar__continue-button');
          if (continueBtn) {
            continueBtn.classList.remove('loading');
          }
        }
      });
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleSubscription);
  } else {
    initBundleSubscription();
  }

    // Re-initialize on Shopify section re-render
  document.addEventListener('shopify:section:load', initBundleSubscription);

  // When user returns via browser back and page is restored from bfcache,
  // re-hydrate bundleBasket from cart so cards and bar show real state (same as F5).
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    const bar = getBar();
    if (!bar || !BUNDLE_ACTIVE_MODES.includes(bar.dataset.mode)) return;
    hydrateBundleBasketFromCart();
  });

  // Expose functions for external use
  window.BundleSubscription.getBundleCount = getBundleCount;
  window.BundleSubscription.updateContinueButton = updateContinueButton;
  window.BundleSubscription.getBundleTotalCount = getBundleTotalCount;
  window.BundleSubscription.getBundleBasketTotalPrice = getBundleBasketTotalPrice;
  window.BundleSubscription.getBundlePrimaryTotalPrice = getBundlePrimaryTotalPrice;
  window.BundleSubscription.getSubscriptionBundleTotalPrice = getSubscriptionBundleTotalPrice;
  window.BundleSubscription.hydrateBundleBasketFromCart = hydrateBundleBasketFromCart;
  window.BundleSubscription.updatePurchaseBar = () => {
    const bar = getBar();
    if (!bar) return;
    ensureBarConfig(bar);
    updatePurchaseBar(bar);
  };
})();
